/**
 * Connector anchors: named attachment points a part carries, such as a stud's
 * top, a socket's mouth or a hinge pin. A part's asset carries each one as an
 * empty node named `anchor:<name>` whose +Y axis points out of the connector,
 * so every copy that shares the asset carries them too.
 *
 * Mating places one part so that its anchor sits on another part's anchor,
 * the two facing each other. A placement then names which connectors join
 * instead of asserting raw coordinates read off bounding boxes.
 */

import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Node } from '@babylonjs/core/node';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { SceneObject } from '../../core/SceneObject';

/** The name prefix of an anchor node. */
export const ANCHOR_PREFIX = 'anchor:';

export interface WorldAnchor {
    name: string;
    position: Vector3;
    /** Unit vector out of the connector. */
    direction: Vector3;
}

function isTransformNode(node: Node): node is TransformNode {
    return 'absolutePosition' in node;
}

/**
 * The anchors a scene object owns, in world space. Anchors inside a child
 * scene object belong to that child, so a group lists none of its parts'.
 */
export function objectAnchors(object: SceneObject): WorldAnchor[] {
    object.transform.computeWorldMatrix(true);
    const anchors: WorldAnchor[] = [];
    for (const node of object.transform.getDescendants(false)) {
        if (!node.name.startsWith(ANCHOR_PREFIX) || !isTransformNode(node)) continue;
        let owner: SceneObject | undefined;
        for (let at: Node | null = node.parent; at && !owner; at = at.parent) owner = object.traceScene.findSceneObjectByTransform(at);
        if (owner !== object) continue;
        node.computeWorldMatrix(true);
        anchors.push({
            name: node.name.slice(ANCHOR_PREFIX.length),
            position: node.getAbsolutePosition().clone(),
            direction: Vector3.TransformNormal(Vector3.Up(), node.getWorldMatrix()).normalize(),
        });
    }
    return anchors;
}

/** The rotation an anchor node carries to point out along unit vector `direction`. */
export function anchorRotation(direction: Vector3): Quaternion {
    return rotationBetween(Vector3.Up(), direction);
}

/** The rotation that turns unit vector `from` onto unit vector `to`. */
function rotationBetween(from: Vector3, to: Vector3): Quaternion {
    const dot = Vector3.Dot(from, to);
    if (dot < -1 + 1e-9) {
        // Opposite: turn half a revolution about any axis perpendicular to `from`.
        const axis = Math.abs(from.x) < 0.9 ? Vector3.Cross(from, Vector3.Right()) : Vector3.Cross(from, Vector3.Up());
        return Quaternion.RotationAxis(axis.normalize(), Math.PI);
    }
    const axis = Vector3.Cross(from, to);
    return new Quaternion(axis.x, axis.y, axis.z, 1 + dot).normalize();
}

/** A world pose. */
export interface Pose { position: Vector3; rotation: Quaternion }

/**
 * Where a part goes so its anchor meets a target anchor, facing it: the part
 * turns the least it must to point its anchor against the target's, spins
 * `spinDegrees` about that shared axis (counter-clockwise looking down the
 * target's direction, as a turn seen from above a stud), then moves until the
 * two points meet. Poses and anchors are world space.
 */
export function matePose(part: Pose, anchor: WorldAnchor, target: WorldAnchor, spinDegrees = 0): Pose {
    const facing = target.direction.scale(-1);
    const turn = Quaternion.RotationAxis(target.direction, spinDegrees * Math.PI / 180)
        .multiply(rotationBetween(anchor.direction, facing));
    const offset = anchor.position.subtract(part.position).applyRotationQuaternion(turn);
    return {
        rotation: turn.multiply(part.rotation).normalize(),
        position: target.position.subtract(offset),
    };
}
